import { put, call, select, getContext, take } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { merge as _merge, get as _get, find as _find } from 'lodash';
import { buildUrl, createErrorRemoveReducer, createErrorClearReducer, decorateEntity } from './utils';
import { template } from '../utils/misc';

export function createFetchListSaga(url, entityDescriptor, defaultOptions) {
  const { logger } = entityDescriptor;
  const urlTemplate = template(url);
  const responseProjection = defaultOptions.responseProjection || (input => input);
  const defaultFilters = {
    limit: 20,
    search: null,
    sort: 'relevance',
    dir: 'desc',
    page: 1,
    ...(defaultOptions.defaultFilters || {}),
  };

  const getLocale = createSelector(
    state => state.intl,
    state => state.locale
  );

  return function* fetchListSaga({ payload: { options = {}, fetchNextChunk } }) {
    try {
      const http = yield getContext('http');
      const state = yield select();
      const locale = yield select(getLocale);
      const listLenght = yield select(entityDescriptor.selectors.getListLength);
      const limit = _get(options, 'filters.limit', defaultFilters.limit);
      const urlParameter = { options };
      const queryParams = _merge(defaultFilters, {
        ...options.filters,
        offset: fetchNextChunk ? listLenght : 0,
        page: fetchNextChunk ? Math.ceil(listLenght / limit) + 1 : 1,
      });
      const requestUrl = buildUrl(urlTemplate(urlParameter), queryParams, locale);
      const requestConfig = defaultOptions.requestConfig
        ? { ...defaultOptions.requestConfig, ...options.requestConfig }
        : options.requestConfig;

      if (!fetchNextChunk) {
        logger.debug('Fetch list URL', url, 'defaultOptions', defaultOptions, 'Options', options);
        logger.debug('Fetch list RequestUrl', requestUrl, 'RequestConfig', requestConfig);
      } else {
        logger.trace('Fetch list URL', url, 'defaultOptions', defaultOptions, 'Options', options);
        logger.trace('Fetch list RequestUrl', requestUrl, 'RequestConfig', requestConfig);
      }

      const response = yield call(http.get, requestUrl, requestConfig);

      if (response.status < 200 || response.status > 299) {
        logger.error('Response with HTTP status', response.status, response);
        yield put(entityDescriptor.fetchListFailure(response.data));
      } else if (fetchNextChunk) {
        yield put(entityDescriptor.fetchListChunkComplete(responseProjection(response.data, state), options));
      } else {
        yield put(entityDescriptor.fetchListComplete(responseProjection(response.data, state)));
      }
    } catch (e) {
      logger.error('Exception occurred while fetching entity list', e);
      yield put(entityDescriptor.fetchListFailure(e));
    }
  };
}

export function createFetchListActionTypes(actionPrefix) {
  return {
    FETCH_LIST: actionPrefix + '_FETCH_LIST',
    FETCH_LIST_COMPLETE: actionPrefix + '_FETCH_LIST_COMPLETE',
    FETCH_LIST_CHUNK_COMPLETE: actionPrefix + '_FETCH_LIST_CHUNK_COMPLETE',
    FETCH_LIST_FAILURE: actionPrefix + '_FETCH_LIST_FAILURE',
    LIST_REMOVE_ERROR: actionPrefix + '_LIST_REMOVE_ERROR',
    LIST_CLEAR_ERRORS: actionPrefix + '_LIST_CLEAR_ERRORS',
    ADD_LIST_ITEM: actionPrefix + '_ADD_LIST_ITEM',
    REMOVE_LIST_ITEM: actionPrefix + '_REMOVE_LIST_ITEM',
  };
}

export function createFetchListActions(actionTypes) {
  return {
    fetchList: (options, fetchNextChunk = false) => {
      return { type: actionTypes.FETCH_LIST, payload: { options, fetchNextChunk } };
    },
    // fetchListLoading: loading => ({ type: actionTypes.FETCH_LIST_LOADING, payload: { loading } }),
    fetchListComplete: data => ({
      type: actionTypes.FETCH_LIST_COMPLETE,
      payload: { data },
    }),
    fetchListChunkComplete: (data, options) => ({
      type: actionTypes.FETCH_LIST_CHUNK_COMPLETE,
      payload: { data, options },
    }),
    fetchListFailure: error => ({
      type: actionTypes.FETCH_LIST_FAILURE,
      payload: { error },
      error: true,
    }),
    listRemoveError: index => ({ type: actionTypes.LIST_REMOVE_ERROR, payload: { index } }),
    listClearErrors: () => ({ type: actionTypes.LIST_CLEAR_ERRORS }),
    addListItem: item => ({ type: actionTypes.ADD_LIST_ITEM, payload: { item } }),
    removeListItem: id => ({ type: actionTypes.REMOVE_LIST_ITEM, payload: { id } }),
  };
}

export function createFetchListReduceFunctions(entityDescriptor) {
  return {
    [entityDescriptor.actionTypes.FETCH_LIST]: (state, { fetchNextChunk }) => {
      return { ...state, listLoading: true, list: fetchNextChunk ? state.list : [] };
    },
    [entityDescriptor.actionTypes.FETCH_LIST_COMPLETE]: (state, { data }) => {
      return {
        ...state,
        count: data.count,
        list: data.list,
        listLoading: false,
      };
    },
    [entityDescriptor.actionTypes.FETCH_LIST_CHUNK_COMPLETE]: (state, { data }) => {
      const offset = data.offset || 0;

      return {
        ...state,
        count: data.count,
        list: state.list.slice(0, offset).concat(data.list),
        listLoading: false,
      };
    },
    [entityDescriptor.actionTypes.FETCH_LIST_FAILURE]: (state, { error }) => ({
      ...state,
      listErrors: [...state.listErrors, error],
      listLoading: false,
    }),
    [entityDescriptor.actionTypes.LIST_REMOVE_ERROR]: createErrorRemoveReducer('listErrors'),
    [entityDescriptor.actionTypes.LIST_CLEAR_ERRORS]: createErrorClearReducer('listErrors'),
    [entityDescriptor.actionTypes.ADD_LIST_ITEM]: (state, { item }) => ({
      ...state,
      count: state.count + 1,
      list: [item, ...state.list],
    }),
    [entityDescriptor.actionTypes.REMOVE_LIST_ITEM]: (state, { id }) => ({
      ...state,
      count: state.count + 1,
      list: state.list.filter(item => item.id !== id),
    }),
  };
}
export function createFetchListSelectors(moduleName, entity) {
  const selectEntity = state => state[moduleName][entity.name];

  return {
    isListLoading: createSelector(selectEntity, entityState => entityState.listLoading),
    getListCount: createSelector(selectEntity, entityState => entityState.count),
    getListErrors: createSelector(selectEntity, entityState => entityState.listErrors),
    hasListErrors: createSelector(selectEntity, entityState => entityState.listErrors.length > 0),
    getList: createSelector(
      selectEntity,
      entity.__decorator
        ? entityState => entityState.list.map(entry => decorateEntity(entry, entity))
        : entityState => entityState.list
    ),
    findOneFromList: id =>
      createSelector(selectEntity, ({ list }) => list.find(item => item[entity.primaryKey] === id)),
    getListLength: createSelector(selectEntity, entityState => entityState.list.length),
    canFetchNextChunk: createSelector(
      selectEntity,
      entityState => !entityState.listLoading && entityState.list.length < entityState.count
    ),
  };
}

function* waitForListSaga(entity) {
  const isLoading = yield select(entity.selectors.isListLoading);

  if (!isLoading) {
    yield put(entity.fetchList());
  }

  yield take([entity.actionTypes.FETCH_LIST_COMPLETE, entity.actionTypes.FETCH_LIST_FAILURE]);

  const list = yield select(entity.selectors.getList);

  return list;
}

export function waitForList(entity) {
  return call(waitForListSaga, entity);
}
